<template>
  <template
    v-for="(item, index) in entries"
    :key="index"
  >
    <div class="field has-addons">
      <div class="control">
        <input
          class="input"
          :class="{ 'is-danger': entryErrors[index] }"
          type="text"
          placeholder="Key"
          required
          :disabled="fieldData.loading || !fieldDefinition.editable || undefined"
          v-model="entries[index][0]"
        />
      </div>
      <div class="control">
        <input
          class="input"
          type="text"
          placeholder="Value"
          required
          :disabled="fieldData.loading || !fieldDefinition.editable || undefined"
          v-model="entries[index][1]"
        />
      </div>
      <div
        class="control"
        v-if="fieldDefinition.editable"
      >
        <button
          type="button"
          class="button has-text-danger"
          v-on:click="remove(index)"
          :disabled="fieldData.loading || undefined"
          title="Remove this key-value pair from the dictionary"
        >
          <i class="icon-minus"></i>
        </button>
      </div>
    </div>
    <template v-if="entryErrors[index]">
      <p
        class="help is-danger"
        v-for="(error, i) in entryErrors[index]"
        :key="i"
      >
        {{ error }}
      </p>
    </template>
  </template>
  <p v-if="!fieldDefinition.editable && !entries.length">&mdash;</p>
  <button
    type="button"
    class="button is-primary"
    v-on:click="add"
    v-if="fieldDefinition.editable"
    :disabled="fieldData.loading || undefined"
    title="Add a key-value pair to this dictionary"
  >
    <i class="icon-plus"></i>
  </button>
  <p
    class="help is-danger"
    v-for="(error, i) in [...localErrors, ...fieldData.errors]"
    :key="i"
  >
    {{ error }}
  </p>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import { type FieldModel, type FieldProps, defineValidator } from '.'

type Constraint = { type: 'dict'; many: false }
const { fieldDefinition } = defineProps<FieldProps<Constraint>>()
const fieldData = defineModel<FieldModel<Constraint>>({ required: true })

const { localErrors } = defineValidator(fieldDefinition, fieldData.value)

/**
 * Array of dictionary entries. They may have duplicate keys, or invalid keys and values.
 */
const entries = ref<[key: string, value: string][]>(Object.entries(fieldData.value.value ?? {}))

/**
 * Errors that are specific to each key-value pair from the `entries` array.
 * This field is considered valid when there are no errors on any pair.
 */
const entryErrors = computed((): Record<number, string[] | undefined> => {
  const seenKeys = new Set<string>()
  return Object.fromEntries(
    entries.value
      .map(([key], index) => {
        if (!key) return [index, ['A key must be set']]
        if (seenKeys.has(key)) return [index, ['Keys must be unique']]
        seenKeys.add(key)
        return null
      })
      .filter((value) => value !== null),
  )
})

watch(
  entries,
  (newValue: [string, string][]) => {
    fieldData.value.value = Object.fromEntries(newValue)
  },
  { deep: true },
)

// Have the dict's `valid` attribute combine the dict's global validation and the one per entries
const dictAndEntriesValid = computed(
  () => localErrors.value.length <= 0 && Object.keys(entryErrors.value).length <= 0,
)
watch(
  dictAndEntriesValid,
  (newValue: boolean) => {
    fieldData.value.valid = newValue
  },
  { immediate: true },
)

function add() {
  if (fieldData.value.loading) return
  entries.value.push(['', ''])
}

function remove(index: number) {
  if (fieldData.value.loading) return
  if (index < 0 || index >= entries.value.length) throw new Error('Index out of range')
  entries.value.splice(index, 1)
}
</script>

<style lang="css" scoped>
.field + .help {
  margin-top: calc(-1 * var(--bulma-block-spacing));
}
</style>
